import React, { useId } from "react"
import cx from "classnames"
import { VisuallyHidden } from "../visually-hidden"
import { matchesExactPath } from "../navigation/navigation-utils"
import { usePageHeaderContext } from "../page-header/page-header-context"
import { PageSecondaryHeaderCenterContent } from "./page-secondary-header-center-content"
import { PageSecondaryHeaderMenu } from "./page-secondary-header-menu"
import type { PageSecondaryHeaderMenuItem } from "./page-secondary-header-menu"
import type { PageSecondaryHeaderTabLinkProps } from "./page-secondary-header-tab-link"

// =============================================================================
// Props
// =============================================================================

export type PageSecondaryHeaderTabLinksProps = {
  accessibleName: string
  children?: React.ReactNode
  className?: string
}

// =============================================================================
// Styles
// =============================================================================

const pageSecondaryHeaderTabLinksStyles = [
  "-my-3",
  "hidden @min-[28rem]/center-content:flex items-center justify-center gap-6"
]

// =============================================================================
// Component
// =============================================================================

/**
 * Secondary page header tab links (cross-page navigation).
 *
 * Renders a `<nav>` landmark for navigation that *visually* looks like tabs but
 * moves the user between pages (each link has its own URL). Compose with
 * `PageSecondaryHeader.TabLink` for each link; the active link is derived from
 * the ancestor `PageHeader`'s `currentPath`.
 *
 * Below `@min-[28rem]/center-content` the links collapse into a dropdown (`Menu`) whose
 * trigger shows the active link.
 *
 * For in-page panel switching (same URL, switching content), use
 * `PageSecondaryHeader.Tabs` instead — it renders a real `role="tablist"` with
 * arrow-key navigation and shared state via `PageSecondaryHeader.TabsRoot`.
 *
 * The accessible name differentiates this navigation landmark from the main
 * `<Navigation>` component (e.g. "Broadcast sections").
 */
export function PageSecondaryHeaderTabLinks({
  accessibleName,
  children,
  className
}: PageSecondaryHeaderTabLinksProps) {
  const accessibleNameId = useId()
  const { currentPath } = usePageHeaderContext()

  const linkChildren = React.Children.toArray(children).filter(
    React.isValidElement
  ) as React.ReactElement<PageSecondaryHeaderTabLinkProps>[]

  const items: PageSecondaryHeaderMenuItem[] = linkChildren.map(child => {
    const { href, children: label, render } = child.props
    // The label may live on the link's children or inside a custom `render`
    // element (e.g. `render={<Link>Build</Link>}` with no direct children).
    const renderElement = React.isValidElement<{ children?: React.ReactNode }>(
      render
    )
      ? render
      : undefined
    return {
      key: href,
      label: label ?? renderElement?.props.children,
      isActive: matchesExactPath(currentPath, href),
      // The menu item injects the label as children at runtime, so the anchor
      // is built without children (and via `createElement` to keep it so).
      render: renderElement ?? React.createElement("a", { href })
    }
  })

  return (
    <PageSecondaryHeaderCenterContent>
      <nav
        className={cx(pageSecondaryHeaderTabLinksStyles, className)}
        aria-labelledby={accessibleNameId}
        data-slot="secondary-tab-links"
      >
        <VisuallyHidden id={accessibleNameId}>{accessibleName}</VisuallyHidden>
        {children}
      </nav>
      <PageSecondaryHeaderMenu
        accessibleName={accessibleName}
        items={items}
        dataSlot="secondary-tab-links-menu"
      />
    </PageSecondaryHeaderCenterContent>
  )
}
